import { useEffect, useState } from "react";
import { Bot, UserRound } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type ConversationAttendingNoticeProps = {
  workspaceId: string | null | undefined;
  assignedMemberId?: string | null;
  assignedMemberIds?: string[];
  conversationState: string;
  className?: string;
};

const MEMBER_CHIP = {
  shell: "border-primary/30 bg-primary/12",
  icon: "text-primary",
  prefix: "text-primary/80",
  name: "text-primary",
} as const;

const IA_CHIP = {
  shell: "border-emerald-500/35 bg-emerald-500/14",
  icon: "text-emerald-600 dark:text-emerald-400",
  prefix: "text-emerald-600/80 dark:text-emerald-400/80",
  name: "text-emerald-700 dark:text-emerald-300",
} as const;

function resolveMemberLabel(
  displayName: string | null | undefined,
  fullName: string | null | undefined,
): string {
  const name = (displayName || fullName || "").trim();
  return name || "Membro da equipe";
}

/** Aviso compacto para admin: membros atribuídos ou Agente de IA quando ativa. */
export function ConversationAttendingNotice({
  workspaceId,
  assignedMemberId,
  assignedMemberIds,
  conversationState,
  className,
}: ConversationAttendingNoticeProps) {
  const [memberNames, setMemberNames] = useState<string[]>([]);
  const iaActive = conversationState === "active";

  const memberIds =
    assignedMemberIds?.length
      ? assignedMemberIds
      : assignedMemberId
        ? [assignedMemberId]
        : [];

  useEffect(() => {
    if (!workspaceId || memberIds.length === 0) {
      setMemberNames([]);
      return;
    }

    let cancelled = false;

    const fetchMembers = async () => {
      const { data: members } = await supabase
        .from("workspace_members")
        .select("id, display_name, user_id")
        .eq("workspace_id", workspaceId)
        .in("id", memberIds);

      if (cancelled) return;

      const orderedMembers = memberIds
        .map((id) => (members ?? []).find((member) => member.id === id))
        .filter((member): member is NonNullable<typeof member> => Boolean(member));

      const userIds = orderedMembers.map((member) => member.user_id).filter(Boolean);
      const { data: profiles } =
        userIds.length > 0
          ? await supabase.from("profiles").select("id, full_name").in("id", userIds)
          : { data: [] as { id: string; full_name: string | null }[] };

      if (cancelled) return;

      const profileMap = new Map((profiles ?? []).map((profile) => [profile.id, profile.full_name]));
      setMemberNames(
        orderedMembers.map((member) =>
          resolveMemberLabel(member.display_name, profileMap.get(member.user_id)),
        ),
      );
    };

    void fetchMembers();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, memberIds.join(",")]);

  const showMember = memberIds.length > 0;
  const showIa = !showMember && iaActive;

  if (!showMember && !showIa) return null;

  const label = showMember
    ? memberNames.length > 0
      ? memberNames.join(", ")
      : "…"
    : "Agente de IA";
  const prefix = showMember ? "Atendido por " : "";
  const title = showMember
    ? `Atendido por ${memberNames.length > 0 ? memberNames.join(", ") : "membro(s) da equipe"}`
    : "Atendimento pelo Agente de IA";

  const palette = showMember ? MEMBER_CHIP : IA_CHIP;

  return (
    <span
      className={cn(
        "inline-flex max-w-[min(100%,20rem)] shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs leading-tight",
        palette.shell,
        className,
      )}
      role="status"
      aria-live="polite"
      title={title}
    >
      {showMember ? (
        <UserRound className={cn("h-3 w-3 shrink-0", palette.icon)} strokeWidth={2.25} aria-hidden />
      ) : (
        <Bot className={cn("h-3 w-3 shrink-0", palette.icon)} strokeWidth={2.25} aria-hidden />
      )}
      <span className="min-w-0 truncate">
        {prefix ? <span className={cn("hidden sm:inline", palette.prefix)}>{prefix}</span> : null}
        <span className={cn("font-semibold", palette.name)}>{label}</span>
      </span>
    </span>
  );
}
